import React, { useState, useEffect, useCallback } from 'react';
import { handlePercentageChange, stripPercentSymbol } from '@utils/percentageInputsUtils';
import {
  convertDecimalToPercentage,
  convertPercentageToDecimal,
} from '../AggregatedCohortTable/utils';

interface UsePercentageFormatOptions {
  value: number | null;
  maxDecimals?: number;
  onChange?: (value: number | null) => void;
}

const usePercentageFormat = ({ value, maxDecimals = 2, onChange }: UsePercentageFormatOptions) => {
  const [formattedValue, setFormattedValue] = useState(
    value !== null ? convertDecimalToPercentage(value, maxDecimals) : '',
  );

  useEffect(() => {
    setFormattedValue(value !== null ? convertDecimalToPercentage(value, maxDecimals) : '');
  }, [value, maxDecimals]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const inputValue = e.target.value || '';

      const sanitizedInput = inputValue.replace(/[^-%.0-9]/g, '');
      if (sanitizedInput !== inputValue) {
        e.preventDefault();
        return;
      }

      if (inputValue === '' || inputValue === '%') {
        setFormattedValue('');
        onChange?.(null);
        return;
      }

      if (inputValue === '-') {
        setFormattedValue('-');
        return;
      }

      const sanitizedValue = stripPercentSymbol(inputValue).toString();
      let truncatedValue = sanitizedValue;
      if (sanitizedValue.includes('.')) {
        const [intPart, decimalPart] = sanitizedValue.split('.');
        truncatedValue = `${intPart}.${decimalPart.slice(0, maxDecimals)}`;
      }

      const newActualValue = convertPercentageToDecimal(truncatedValue);

      handlePercentageChange(e, setFormattedValue);
      onChange?.(newActualValue);
    },
    [onChange, maxDecimals],
  );

  return { formattedValue, handleChange };
};

export default usePercentageFormat;
